import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useState } from 'react';

import { toDatetime } from 'src/legacy/Utils/Conversor';

import { RouteParamsEvent } from 'src/types/routing';

import { useEventContext } from '../../provider';
import { useCheckoutStore } from '../../stores';
import {
  useEventHistory,
  useModalExpiration,
  useNextEventButton,
  useNextLabelButton,
} from '../../hooks';

import CheckoutTimer from './Timer';
import { Box, Button, Icon, Text } from '@chakra-ui/react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { EllipsisLoading } from 'src/components/loadings';
import { useGetCheckoutRoutesVerification } from '../../hooks/useGetCheckoutRoutesVerification';

const CheckoutFooter = () => {
  const { eventID } = useParams<RouteParamsEvent>();
  const { isNextButtonLoading } = useEventContext();
  const { getEventCheckout, clearEventCheckout } = useCheckoutStore();
  const eventHistory = useEventHistory();
  const navigate = useNavigate();
  const [hasExpired, setHasExpired] = useState(false);

  const { isTicketsRoute, isPaymentRoute, isLoginRoute } = useGetCheckoutRoutesVerification();

  const [onNext, isLoading] = useNextEventButton();
  const modalExpiration = useModalExpiration();

  const checkout = getEventCheckout(eventID!);

  const nextText = useNextLabelButton();

  const hasValidCheckout = () => {
    if (!checkout.sellId) return false;

    const currentTime = new Date();
    const expirationDate = toDatetime(checkout.expirationDate);

    return currentTime <= expirationDate;
  };

  const onExpire = useCallback(() => {
    if (!eventID) return;

    // Adiciona flag genérica para prevenir reabertura automática em qualquer circunstância
    sessionStorage.setItem('checkout_expired', 'true');
    // Isso permite que cada aba com um checkout diferente tenha seu próprio controle de expiração
    const checkoutKey = `checkout_expired_${checkout.sellId}`;
    sessionStorage.setItem(checkoutKey, 'true');

    setHasExpired(true);
    clearEventCheckout(eventID);
    modalExpiration.openAsync({
      eventId: eventID,
      title: 'Seu tempo acabou',
      content:
        'O seu tempo para comprar os ingressos acabou e os ingressos que estavam reservados agora estão disponíveis para compra por outros usuários, é necessário recarregar a página para tentar obter os ingressos novamente.',
    });
  }, [eventID, checkout.sellId, clearEventCheckout, modalExpiration]);

  return (
    <Box
      pb="8"
      display="inline-block"
      width="full"
      minHeight="50px"
      bgColor="white">
      {hasValidCheckout() && !hasExpired && (
        <CheckoutTimer
          expiresAt={toDatetime(checkout.expirationDate)}
          openAt={toDatetime(checkout.openedDate)}
          onExpire={onExpire}
        />
      )}
      <Box
        textAlign="center"
        pt="3"
        display="flex"
        gap="3"
        alignItems="center"
        justifyContent="center">
        <Button
          color="gray"
          display="flex"
          alignItems="center"
          justifyContent="center"
          className="prev"
          rounded="full"
          onClick={() => {
            if (isTicketsRoute) {
              eventHistory.push(`/event/${eventID}`);
              return;
            }

            if (isPaymentRoute || isLoginRoute) {
              eventHistory.push(`/event/${eventID}/checkout/tickets`);
              return;
            }

            navigate(-1);
          }}>
          <Icon as={FaArrowLeft} />
        </Button>

        <Button
          colorScheme="brand"
          className="next"
          // TODO: Investigar a tipagem do onNext.. Aparenta estar errada..
          // @ts-ignore
          onClick={() => onNext()}
          disabled={isLoading || isNextButtonLoading}
          display="flex"
          alignItems="center"
          justifyContent="center"
          rounded="full"
          isLoading={isLoading || isNextButtonLoading}
          spinner={<EllipsisLoading color="white" />}>
          <Text display="inline" textTransform="uppercase" mr="2">
            {nextText}
          </Text>
          <Icon as={FaArrowRight} alignSelf="center" justifySelf="center" />
        </Button>
      </Box>
    </Box>
  );
};

export default CheckoutFooter;
